<template>
    <div class="ttc-page">

        <!-- PAGE HEADER -->
        <!-- <div class="ttc-header">
            <div>
                <h1 class="ttc-title">Types de travaux</h1>
                <p class="ttc-subtitle">
                    Sélectionnez une catégorie puis un type d'intervention pour gérer les types de travaux.<br />
                    Les types de travaux permettent aux techniciens de préciser ce qu'ils ont réalisé lors d'une intervention.
                </p>
            </div>
        </div> -->

        <!-- LOADER -->
        <div v-if="loading" class="itc-loader-wrap">
            <div class="flex flex-col items-center justify-center py-24">
                <div class="flex gap-2 mb-4">
                    <div class="w-2 h-2 bg-orange-600 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                    <div class="w-2 h-2 bg-orange-600 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                    <div class="w-2 h-2 bg-orange-600 rounded-full animate-bounce"></div>
                </div>
                <p class="text-sm text-gray-500">Chargement des types de travaux…</p>
            </div>
        </div>

        <!-- LAYOUT : sidebar-cat | sidebar-type | main -->
        <div v-else class="ttc-layout">

            <!-- SIDEBAR 1 : CATÉGORIES -->
            <div class="ttc-sidebar">
                <div class="ttc-sidebar-header">Catégories</div>
                <div class="ttc-folder-list">
                    <div
                        v-for="cat in categories"
                        :key="cat.key"
                        class="ttc-folder-item"
                        :class="{ active: activeCategory === cat.key }"
                        @click="selectCategory(cat.key)">
                        <img
                            v-if="cat.icon"
                            :src="cat.icon"
                            class="ttc-folder-icon"
                            alt="" />
                        <span class="ttc-folder-label">{{ cat.label }}</span>
                        <span class="ttc-folder-count">{{ countForCategory(cat.key) }}</span>
                    </div>
                </div>
            </div>

            <!-- SIDEBAR 2 : TYPE D'INTERVENTION -->
            <div class="ttc-sidebar ttc-sidebar--sub" :class="{ 'ttc-sidebar--hidden': !activeCategory }">
                <div class="ttc-sidebar-header">Type d'intervention</div>
                <div class="ttc-folder-list">
                    <div
                        v-for="t in typesLogiques"
                        :key="t.value"
                        class="ttc-folder-item ttc-folder-item--sub"
                        :class="{ active: activeTypeLogique === t.value }"
                        @click="selectTypeLogique(t.value)">
                        <span class="ttc-folder-label">{{ t.label }}</span>
                        <span class="ttc-folder-count">{{ countForTypeLogique(t.value) }}</span>
                    </div>
                </div>
            </div>

            <!-- MAIN CONTENT -->
            <div class="ttc-main">

                <!-- ÉTAT 1 : aucune catégorie -->
                <div v-if="!activeCategory" class="ttc-no-select">
                    <div class="ttc-no-select-icon"><i class="fas fa-folder-open"></i></div>
                    <p>Sélectionnez une catégorie<br />pour continuer</p>
                </div>

                <!-- ÉTAT 2 : catégorie ok, pas de type -->
                <div v-else-if="!activeTypeLogique" class="ttc-no-select">
                    <div class="ttc-no-select-icon"><i class="fas fa-list-ul"></i></div>
                    <p>Sélectionnez un type d'intervention<br />pour voir les types de travaux associés</p>
                </div>

                <!-- ÉTAT 3 : tout sélectionné -->
                <template v-else>

                    <!-- BREADCRUMB -->
                    <div class="ttc-breadcrumb">
                        <img
                            v-if="activeCategoryObj && activeCategoryObj.icon"
                            :src="activeCategoryObj.icon"
                            class="ttc-breadcrumb-icon" />
                        <span class="ttc-breadcrumb-cat">{{ activeCategoryObj && activeCategoryObj.label }}</span>
                        <i class="fas fa-chevron-right ttc-breadcrumb-sep"></i>
                        <span class="ttc-breadcrumb-type">{{ activeTypeLogiqueObj && activeTypeLogiqueObj.label }}</span>
                    </div>

                    <!-- TOOLBAR -->
                    <div class="ttc-toolbar">
                        <div class="ttc-search-wrap">
                            <i class="fas fa-search ttc-search-icon"></i>
                            <input
                                v-model="filters.search"
                                type="text"
                                class="ttc-search-input"
                                placeholder="Rechercher un type de travaux..." />
                        </div>
                    </div>

                    <!-- INFO BANNER -->
                    <div class="ttc-info-banner">
                        <i class="fas fa-info-circle ttc-info-icon"></i>
                        <p>Cliquez directement sur les cellules du tableau pour éditer. L'enregistrement est automatique.</p>
                    </div>

                    <!-- TABLE -->
                    <div class="ttc-table-wrap">
                        <table class="ttc-table">
                            <thead>
                                <tr>
                                    <th style="width:75%">Libellé</th>
                                    <th style="width:25%;text-align:right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>

                                <!-- CREATE ROW -->
                                <tr class="ttc-create-row">
                                    <td colspan="2">
                                        <div class="ttc-create-inline">
                                            <input
                                                v-model="create.libelle"
                                                class="ttc-cell-input ttc-cell-input--create"
                                                placeholder="Nouveau type de travaux..."
                                                @keydown.enter="storeNew" />
                                            <button
                                                class="ttc-btn-add"
                                                :disabled="!canCreate"
                                                @click="storeNew">
                                                <i class="fas fa-plus"></i>
                                                Ajouter
                                            </button>
                                        </div>
                                    </td>
                                </tr>

                                <!-- EMPTY STATE -->
                                <tr v-if="filteredItems.length === 0">
                                    <td colspan="2">
                                        <div class="ttc-empty">
                                            <div class="ttc-empty-icon"><i class="fas fa-inbox"></i></div>
                                            <strong>{{ filters.search ? 'Aucun résultat' : 'Aucun type de travaux ici' }}</strong>
                                            <p>{{ filters.search ? 'Essayez de modifier votre recherche.' : 'Ajoutez le premier type de travaux ci-dessus.' }}</p>
                                        </div>
                                    </td>
                                </tr>

                                <!-- EXISTING ROWS -->
                                <tr
                                    v-for="row in filteredItems"
                                    :key="row.id"
                                    class="ttc-row">

                                    <td>
                                        <input
                                            class="ttc-cell-input"
                                            :value="row.libelle"
                                            @blur="onLibelleBlur(row, $event)"
                                            @keydown.enter="$event.target.blur()" />
                                    </td>

                                    <td>
                                        <div class="ttc-action-cell">
                                            <button
                                                class="ttc-btn-icon ttc-btn-danger"
                                                @click="remove(row)"
                                                title="Supprimer">
                                                <i class="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    </td>

                                </tr>
                            </tbody>
                        </table>
                    </div>

                </template>
            </div>
        </div>

    </div>
</template>

<script>
import TypeLogiqueIntervention from '@/config/TypeLogiqueIntervention'

export default {
    name: 'TravauxTypesCrud',

    data() {
        return {
            items: [],
            categories: [],
            typesLogiques: TypeLogiqueIntervention,

            activeCategory: null,
            activeTypeLogique: null,

            create: { libelle: '' },
            filters: { search: '' },

            loading: false,
        }
    },

    computed: {
        activeCategoryObj() {
            if (!this.activeCategory) return null
            return this.categories.find(c => c.key === this.activeCategory) || null
        },

        activeTypeLogiqueObj() {
            if (!this.activeTypeLogique) return null
            return this.typesLogiques.find(t => t.value === this.activeTypeLogique) || null
        },

        filteredItems() {
            const search = this.filters.search.toLowerCase()
            return this.items.filter(r => {
                if (r.categorie !== this.activeCategory) return false
                if (r.type_logique !== this.activeTypeLogique) return false
                if (search && !r.libelle.toLowerCase().includes(search)) return false
                return true
            })
        },

        canCreate() {
            return !!(this.create.libelle && this.create.libelle.trim())
        },
    },

    methods: {
        countForCategory(key) {
            return this.items.filter(r => r.categorie === key).length
        },

        countForTypeLogique(value) {
            return this.items.filter(r => r.categorie === this.activeCategory && r.type_logique === value).length
        },

        selectCategory(key) {
            if (this.activeCategory === key) return
            this.activeCategory = key
            this.activeTypeLogique = null
            this.filters.search = ''
            this.create.libelle = ''
        },

        selectTypeLogique(value) {
            this.activeTypeLogique = value
            this.filters.search = ''
            this.create.libelle = ''
        },

        async load() {
            this.loading = true
            const res = await this.$request.post('intervention-travaux/list')
            this.items = Array.isArray(res) ? res : res.items || res.data || []
            this.categories = await this.$request.post('/reference/get-categories', {})
            this.loading = false
        },

        async storeNew() {
            if (!this.canCreate) return
            const row = await this.$request.post('intervention-travaux/store', {
                libelle: this.create.libelle.trim(),
                categorie: this.activeCategory,
                type_logique: this.activeTypeLogique,
            })
            this.items.unshift(row.item)
            this.create.libelle = ''
        },

        async updateCell(id, field, value) {
            if (!id || !field) return
            await this.$request.post('intervention-travaux/update-cell', { id, field, value })
        },

        async remove(row) {
            if (!confirm(`Supprimer « ${row.libelle} » ?`)) return
            await this.$request.post('intervention-travaux/delete', { id: row.id })
            this.items = this.items.filter(i => i.id !== row.id)
        },

        onLibelleBlur(row, event) {
            const val = event.target.value.trim()
            if (val && val !== row.libelle) {
                row.libelle = val
                this.updateCell(row.id, 'libelle', val)
            }
        },
    },

    mounted() {
        this.load()
    },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

.ttc-page {
    font-family: 'DM Sans', sans-serif;
    padding: 32px;
    min-height: 100vh;
    background: #F5F4F1;
    color: #1A1916;
}

/* ── HEADER ──────────────────────────────────── */
.ttc-header { margin-bottom: 28px; }
.ttc-title { font-size: 22px; font-weight: 600; letter-spacing: -0.3px; color: #1A1916; margin: 0; }
.ttc-subtitle { font-size: 13.5px; color: #6B6860; margin-top: 4px; line-height: 1.6; }

/* ── LAYOUT : 3 colonnes ─────────────────────── */
.ttc-layout {
    display: grid;
    grid-template-columns: 200px 180px 1fr;
    gap: 0;
    align-items: start;
    background: #ffffff;
    border: 1px solid #E4E2DC;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(0,0,0,0.07);
}

/* ── SIDEBARS ────────────────────────────────── */
.ttc-sidebar {
    border-right: 1px solid #E4E2DC;
    min-height: 480px;
    background: #FAFAF8;
}

/* Sidebar 2 : grisée et non interactive quand aucune catégorie */
.ttc-sidebar--sub {
    background: #F7F6F3;
    transition: opacity 0.2s;
}
.ttc-sidebar--hidden {
    opacity: 0.4;
    pointer-events: none;
}

.ttc-sidebar-header {
    padding: 13px 14px;
    border-bottom: 1px solid #E4E2DC;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #A8A49D;
    background: inherit;
}

.ttc-folder-list { padding: 6px; }

.ttc-folder-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    font-size: 13px;
    font-weight: 450;
    color: #6B6860;
    user-select: none;
}
.ttc-folder-item:hover { background: #F0EFE9; color: #1A1916; }
.ttc-folder-item.active { background: #1A1916; color: #ffffff; }

/* Sidebar 2 : items actifs en gris foncé doux pour différencier */
.ttc-folder-item--sub.active { background: #3D3C39; color: #ffffff; }

.ttc-folder-icon { width: 16px; height: 16px; object-fit: contain; flex-shrink: 0; opacity: 0.7; }
.ttc-folder-item.active .ttc-folder-icon { opacity: 1; filter: brightness(0) invert(1); }

.ttc-folder-label { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ttc-folder-count {
    font-size: 10.5px;
    font-family: 'DM Mono', monospace;
    background: rgba(0,0,0,0.07);
    border-radius: 20px;
    padding: 1px 6px;
    font-weight: 500;
    flex-shrink: 0;
}
.ttc-folder-item.active .ttc-folder-count { background: rgba(255,255,255,0.2); }

/* ── MAIN ────────────────────────────────────── */
.ttc-main {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 20px;
    background: #ffffff;
    min-height: 480px;
}

/* ── NO SELECT ───────────────────────────────── */
.ttc-no-select {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 24px;
    text-align: center;
}
.ttc-no-select-icon { font-size: 28px; color: #D4D1CB; margin-bottom: 12px; }
.ttc-no-select p { font-size: 13.5px; color: #B0ACA5; line-height: 1.6; }

/* ── BREADCRUMB ──────────────────────────────── */
.ttc-breadcrumb {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 12.5px;
}
.ttc-breadcrumb-icon { width: 14px; height: 14px; object-fit: contain; opacity: 0.45; }
.ttc-breadcrumb-cat { color: #6B6860; }
.ttc-breadcrumb-sep { font-size: 8px; color: #C8C4BC; }
.ttc-breadcrumb-type { color: #1A1916; font-weight: 600; }

/* ── TOOLBAR ─────────────────────────────────── */
.ttc-toolbar { display: flex; align-items: center; gap: 10px; }
.ttc-search-wrap { position: relative; flex: 1; }
.ttc-search-icon {
    position: absolute; left: 11px; top: 50%;
    transform: translateY(-50%);
    color: #A8A49D; font-size: 11px; pointer-events: none;
}
.ttc-search-input {
    width: 100%;
    background: #F5F4F1;
    border: 1px solid #E4E2DC;
    border-radius: 6px;
    padding: 8px 12px 8px 30px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    color: #1A1916;
    outline: none;
    transition: border-color 0.15s, background 0.15s;
}
.ttc-search-input:focus { border-color: #C8C4BC; background: #fff; }
.ttc-search-input::placeholder { color: #C8C4BC; }

/* ── INFO BANNER ─────────────────────────────── */
.ttc-info-banner {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #EFF6FF;
    border-left: 3px solid #3B82F6;
    border-radius: 0 6px 6px 0;
    padding: 9px 13px;
    font-size: 12.5px;
    color: #374151;
}
.ttc-info-icon { color: #3B82F6; font-size: 13px; flex-shrink: 0; }

/* ── TABLE ───────────────────────────────────── */
.ttc-table-wrap {
    border: 1px solid #E4E2DC;
    border-radius: 8px;
    overflow: hidden;
    animation: ttcFadeIn 0.18s ease;
}
@keyframes ttcFadeIn {
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0); }
}

.ttc-table { width: 100%; border-collapse: collapse; }
.ttc-table thead tr { background: #FAFAF8; border-bottom: 1px solid #E4E2DC; }
.ttc-table th {
    text-align: left;
    padding: 10px 14px;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: #A8A49D;
}
.ttc-table td { padding: 0; border-bottom: 1px solid #E4E2DC; vertical-align: middle; }
.ttc-table tbody tr:last-child td { border-bottom: none; }

/* CREATE ROW */
.ttc-create-row td { background: #FFFDF0; border-bottom: 2px solid #E8D88A; }
.ttc-create-inline { display: flex; align-items: center; }
.ttc-create-inline .ttc-cell-input--create { flex: 1; }
.ttc-create-inline .ttc-btn-add { margin-right: 10px; flex-shrink: 0; }

/* ROWS */
.ttc-row { transition: background 0.1s; }
.ttc-row:hover td { background: #FAFAF8; }

/* CELLS */
.ttc-cell-input {
    width: 100%;
    display: block;
    background: transparent;
    border: none;
    padding: 12px 14px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 450;
    color: #1A1916;
    outline: none;
}
.ttc-cell-input:focus { background: #FFFDF5; box-shadow: inset 0 0 0 1px #E8D88A; }
.ttc-cell-input--create::placeholder { color: #B8A96A; font-style: italic; }

/* ACTIONS */
.ttc-action-cell {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    padding: 0 14px;
}
.ttc-btn-add {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #D63B2F;
    color: #ffffff;
    border: none;
    border-radius: 6px;
    padding: 6px 13px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s;
    white-space: nowrap;
}
.ttc-btn-add:hover { background: #B8302A; }
.ttc-btn-add:disabled { background: #E4E2DC; color: #A8A49D; cursor: not-allowed; }

.ttc-btn-icon {
    background: none;
    border: none;
    cursor: pointer;
    padding: 5px 7px;
    border-radius: 5px;
    color: #A8A49D;
    font-size: 12px;
    transition: background 0.15s, color 0.15s;
    line-height: 1;
}
.ttc-btn-icon:hover { background: #F5F4F1; color: #1A1916; }
.ttc-btn-danger:hover { background: #FDF1F0 !important; color: #D63B2F !important; }

/* EMPTY */
.ttc-empty { padding: 48px 24px; text-align: center; }
.ttc-empty-icon { font-size: 28px; color: #C8C4BC; margin-bottom: 12px; }
.ttc-empty strong { display: block; font-size: 14px; font-weight: 500; color: #6B6860; margin-bottom: 5px; }
.ttc-empty p { font-size: 13px; color: #A8A49D; }
</style>